import React from 'react'
import { Case, Default, Switch } from 'react-if'
import { Button } from '@/atoms/Button'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { PictureFrameIcon } from '@/atoms/Icons/PictureFrameIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { TitleXXS } from '@/atoms/Text'
import { paths } from '@/constants'
import { useToast } from '@/molecules/Toast'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { OwnedFramesList, useUserCollectibles } from '@/services/NftService'
import { useUser } from '@/services/UserService'
import { Frame, GetMomentOwnersQuery } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { getUserDisplayName } from '@/utils/users'
import { frameFormatter } from '@/views/AccountView/CollectiblesUnboxModal/CollectiblesUnboxingSceneDetails/CollectiblesUnboxingSceneDetails'

type UserStory = NonNullable<GetMomentOwnersQuery['moment']>['userStory']

interface UserOwnershipSectionProps {
  projectName?: string
  myStory: UserStory
}

export const UserOwnershipSection: React.FC<UserOwnershipSectionProps> = ({ projectName }) => {
  const { t } = useTranslate('watch')
  const track = useSafeTrack()
  const { user } = useUser()
  const { claimedFrames, signedClaimedFrames } = useUserCollectibles()
  const { engagementMoment } = useCollectiblesPlayerEngagement()
  const toast = useToast()

  const userOwnedMoments = [...claimedFrames, ...signedClaimedFrames] as OwnedFramesList['niftys']

  const userFrame = userOwnedMoments.find((m) => {
    return m?.frames?.find((f) => f?.moment?.id === engagementMoment?.id)
  })?.frames?.[0]

  const shareUrl = `${paths.getBaseURL()}${paths.share.moment}/${engagementMoment?.id}/frame/${userFrame?.id}`

  const formatedFrame = frameFormatter(userFrame?.smpteTimecode || '00-00-00-00')

  const userName = getUserDisplayName(user, t('anonymous', 'Anonymous'))
  const linkCopied = t('linkCopied', 'Link copied.')

  const trackShareClick = (network: string) => {
    track('Frame Share Clicked', {
      type: network,
      momentId: engagementMoment?.id,
      momentTitle: engagementMoment?.title,
      projectSlug: engagementMoment?.projectSlug,
      episodeGuid: engagementMoment?.episode?.guid,
      frameId: userFrame?.id,
    })
  }

  const fanOf = projectName ?? 'Angel Studios'

  const findSignature = (frame: Frame | undefined | null): string | null => {
    if (frame) {
      for (const signature of frame?.nifty?.signatures ?? []) {
        if (signature?.signatureUrl !== null) {
          return signature?.signatureUrl ?? null
        }
      }
      return null
    }
    return null
  }

  const hasSignature = findSignature(userFrame) !== null

  return (
    <>
      <div className="w-[210px] px-2 py-3">
        <div className="flex items-center">
          <div className="flex flex-wrap gap-2">
            <div className="mr-2 flex items-center">
              <PictureFrameIcon color="gray-100" className="mr-2" />
              <TitleXXS color="gray-100">
                <Switch>
                  <Case condition={!!userName}>
                    <Translate i18nKey="yourFrame" t={t}>
                      Your Frame
                    </Translate>
                  </Case>
                  <Default>
                    <Translate t={t} i18nKey="fanOfProject">
                      {{ fanOf }}: Fan
                    </Translate>
                  </Default>
                </Switch>
              </TitleXXS>
            </div>
            {userFrame && userFrame.moment && (
              <TitleXXS color="gray-100">
                <InternalLink href={`/share/moment/${userFrame.moment.id}/frame/${userFrame.id}`} className="underline">
                  {formatedFrame}
                </InternalLink>
              </TitleXXS>
            )}
          </div>
        </div>
      </div>
      <div className="flex max-w-sm flex-wrap items-start justify-between gap-1">
        <div className="relative h-[118px] w-[210px]">
          <Image src={userFrame?.uri || ''} alt="" height="118" width="210" className="absolute top-0 rounded-xl" />
          {hasSignature && (
            <img src={findSignature(userFrame) ?? ''} alt="Personalized signature" className="absolute top-0" />
          )}
        </div>
        <div>
          <Button
            onClick={() => {
              trackShareClick('Copy to Clipboard')
              navigator.clipboard.writeText(shareUrl as string)
              toast?.showToast(linkCopied, { icon: <CopyIcon size={24} color="gray-200" /> })
            }}
            variant="black"
          >
            Share Frame
          </Button>
        </div>
      </div>
    </>
  )
}
